import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal, { apparifySwalVariants } from "../../../utils/apparifySwal";
import { ChevronDown, FolderTree, Plus, Shield, Tag, Tags } from "lucide-react";

import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { BtnPrimary, BtnSecondary, EmptyState, FormModal, PageHeader } from "../../components/erp";
import { businessProductsAPI } from "../../services/businessProductsService";
import { businessSwalBase } from "../../utils/businessSwal";
import { useBusinessAuth } from "../../context/BusinessAuthContext";

const inputCls = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-600 dark:focus:ring-neutral-800";
const textareaCls = `${inputCls} min-h-[96px] resize-y`;

function normalizeList(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function initialCategoryForm(category = null) {
  return {
    name: category?.name || "",
    position: category?.position != null ? String(category.position) : "0",
    description: category?.description || "",
  };
}

function initialTagForm(tag = null) {
  return {
    name: tag?.name || "",
    description: tag?.description || "",
  };
}

function UsageBadge({ count, label = "products" }) {
  return (
    <span className="inline-flex rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-xs font-medium text-neutral-700 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-200">
      {count} {label}
    </span>
  );
}

function ProductLinkCard({ product }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/40">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {product.name || `Product #${product.id}`}
          </div>
          <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Status {product.status || "DRAFT"} - Sold {Number(product.soldQty || 0).toLocaleString("en-PH")}
          </div>
        </div>
        <Link
          to={`/business/products/${product.id}`}
          className="rounded-xl border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
        >
          Open Product
        </Link>
      </div>
    </div>
  );
}

export default function MerchandisingCategoriesTags() {
  const auth = useBusinessAuth();
  const authRole = String(auth?.role || "").toUpperCase();
  const hasAnyPermission = useCallback((permissionNames = []) => (
    authRole === "OWNER" || permissionNames.some((permissionName) => auth?.hasPermission?.(permissionName))
  ), [auth, authRole]);
  const canReadCategories = hasAnyPermission(["categories:read", "products:read"]);
  const canCreateCategories = hasAnyPermission(["categories:create", "products:create"]);
  const canEditCategories = hasAnyPermission(["categories:update", "products:update"]);
  const canArchiveCategories = hasAnyPermission(["categories:delete", "products:delete"]);
  const canReadTags = hasAnyPermission(["product_tags:read", "products:read"]);
  const canCreateTags = hasAnyPermission(["product_tags:create", "products:create"]);
  const canEditTags = hasAnyPermission(["product_tags:update", "products:update"]);
  const canArchiveTags = hasAnyPermission(["product_tags:delete", "products:delete"]);
  const canReadProducts = hasAnyPermission(["products:read"]);
  const canAccessPage = canReadCategories || canReadTags || canReadProducts;
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm());
  const [tagForm, setTagForm] = useState(initialTagForm());
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingTag, setSavingTag] = useState(false);

  const load = useCallback(async () => {
    if (!canAccessPage) {
      setCategories([]);
      setTags([]);
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [categoriesRes, tagsRes, productsRes] = await Promise.all([
        canReadCategories ? businessProductsAPI.getCategories().catch(() => []) : Promise.resolve([]),
        canReadTags ? businessProductsAPI.getTags().catch(() => []) : Promise.resolve([]),
        canReadProducts ? businessProductsAPI.getProductSalesReport().catch(() => []) : Promise.resolve([]),
      ]);
      setCategories(normalizeList(categoriesRes));
      setTags(normalizeList(tagsRes));
      setProducts(normalizeList(productsRes));
    } catch {
      setCategories([]);
      setTags([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [canAccessPage, canReadCategories, canReadProducts, canReadTags]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const categoryRows = categories.map((category) => ({
      ...category,
      usageCount: Number(category.product_count || 0),
      isSystem: Boolean(category.is_protected_default) || category.business_id == null,
    }));
    const tagRows = tags.map((tag) => ({
      ...tag,
      usageCount: Number(tag.product_count || 0),
    }));

    return {
      categories: categoryRows,
      systemCategories: categoryRows.filter((category) => category.isSystem),
      customCategories: categoryRows.filter((category) => !category.isSystem),
      tags: tagRows,
      uncategorizedProducts: products.filter((product) => product.category_id == null),
    };
  }, [categories, products, tags]);

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm(initialCategoryForm());
    setCategoryModalOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm(initialCategoryForm(category));
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryForm(initialCategoryForm());
  };

  const openCreateTag = () => {
    setEditingTag(null);
    setTagForm(initialTagForm());
    setTagModalOpen(true);
  };

  const openEditTag = (tag) => {
    setEditingTag(tag);
    setTagForm(initialTagForm(tag));
    setTagModalOpen(true);
  };

  const closeTagModal = () => {
    setTagModalOpen(false);
    setEditingTag(null);
    setTagForm(initialTagForm());
  };

  const saveCategory = async () => {
    if (!(editingCategory?.id ? canEditCategories : canCreateCategories)) return;
    const name = String(categoryForm.name || "").trim();
    const description = String(categoryForm.description || "").trim();
    const position = Number(categoryForm.position || 0);

    if (!name) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: "Category name required",
        text: "Enter a category name before saving.",
      });
      return;
    }
    if (!Number.isInteger(position) || position < 0) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: "Invalid category position",
        text: "Display position must be a whole number at or above zero.",
      });
      return;
    }
    if (description.length > 500) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: "Description too long",
        text: "Category description must be 500 characters or fewer.",
      });
      return;
    }

    setSavingCategory(true);
    try {
      if (editingCategory?.id) {
        await businessProductsAPI.updateCategory(editingCategory.id, { name, description, position });
      } else {
        await businessProductsAPI.createCategory({ name, description, position });
      }
      closeCategoryModal();
      await load();
    } catch (err) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: editingCategory?.id ? "Update failed" : "Create failed",
        text: err?.message || "Unable to save the category record.",
      });
    } finally {
      setSavingCategory(false);
    }
  };

  const saveTag = async () => {
    if (!(editingTag?.id ? canEditTags : canCreateTags)) return;
    const name = String(tagForm.name || "").trim();
    const description = String(tagForm.description || "").trim();

    if (!name) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: "Tag name required",
        text: "Enter a tag name before saving.",
      });
      return;
    }
    if (description.length > 255) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: "Description too long",
        text: "Tag description must be 255 characters or fewer.",
      });
      return;
    }

    setSavingTag(true);
    try {
      if (editingTag?.id) {
        await businessProductsAPI.updateTag(editingTag.id, { name, description });
      } else {
        await businessProductsAPI.createTag({ name, description });
      }
      closeTagModal();
      await load();
    } catch (err) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: editingTag?.id ? "Update failed" : "Create failed",
        text: err?.message || "Unable to save the tag record.",
      });
    } finally {
      setSavingTag(false);
    }
  };

  const archiveCategory = async (category) => {
    if (!canArchiveCategories) return;
    const result = await Swal.fire({
      ...businessSwalBase,
      icon: "warning",
      title: "Archive category?",
      text: `This will remove "${category.name}" from active product-category selection.`,
      showCancelButton: true,
      confirmButtonText: "Archive",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      await businessProductsAPI.deactivateCategory(category.id);
      await load();
    } catch (err) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: "Archive failed",
        text: err?.message || "Unable to archive the category.",
      });
    }
  };

  const archiveTag = async (tag) => {
    if (!canArchiveTags) return;
    const result = await Swal.fire({
      ...businessSwalBase,
      ...apparifySwalVariants.archive,
      icon: "warning",
      title: "Archive tag?",
      text: `This will remove "${tag.name}" from reusable product tags.`,
      showCancelButton: true,
      confirmButtonText: "Archive",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      await businessProductsAPI.archiveTag(tag.id);
      await load();
    } catch (err) {
      await Swal.fire({
        ...businessSwalBase,
        icon: "error",
        title: "Archive failed",
        text: err?.message || "Unable to archive the tag.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Merchandising"
        title="Categories & Tags"
        subtitle="Manage default system categories, business-owned custom categories, and reusable product tags for the merchandising catalog."
      >
        <div className="flex flex-wrap gap-2">
          {canCreateTags ? (
            <BtnSecondary onClick={openCreateTag}>
              <Plus className="h-4 w-4" /> New Tag
            </BtnSecondary>
          ) : null}
          {canCreateCategories ? (
            <BtnPrimary onClick={openCreateCategory}>
              <Plus className="h-4 w-4" /> New Category
            </BtnPrimary>
          ) : null}
        </div>
      </PageHeader>

      {!canAccessPage ? (
        <Panel title="Categories & Tags" subtitle="Product taxonomy management is restricted for this role.">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
            You do not have permission to view product categories or product tags.
          </div>
        </Panel>
      ) : (
        <>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Default Categories" value={loading ? "-" : summary.systemCategories.length} icon={Shield} />
        <StatCard title="Custom Categories" value={loading ? "-" : summary.customCategories.length} icon={FolderTree} />
        <StatCard title="Reusable Tags" value={loading ? "-" : summary.tags.length} icon={Tags} />
        <StatCard title="Uncategorized Products" value={loading ? "-" : summary.uncategorizedProducts.length} icon={Tag} accent />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Panel title="Categories" subtitle="Default system categories stay read-only. Business custom categories stay tenant-local and archiveable.">
          {!canReadCategories ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
              You do not have permission to view product categories.
            </div>
          ) : loading ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading categories...</div>
          ) : !summary.categories.length ? (
            <EmptyState
              icon={FolderTree}
              title="No categories available"
              description="Create a business category or rely on the default system categories to classify products."
            />
          ) : (
            <div className="space-y-5">
              <details className="group overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900/40">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-3.5 hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-neutral-500 dark:hover:bg-neutral-800/60 [&::-webkit-details-marker]:hidden">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">Default System Categories</span>
                    <span className="block text-xs text-neutral-500 dark:text-neutral-400">Read-only defaults available to all businesses</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <UsageBadge count={summary.systemCategories.length} label="records" />
                    <ChevronDown size={16} className="text-neutral-500 transition-transform group-open:rotate-180" aria-hidden="true" />
                  </span>
                </summary>
                <div className="border-t border-neutral-200 dark:border-neutral-800">
                  {!summary.systemCategories.length ? (
                    <div className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">No default system categories are currently available.</div>
                  ) : (
                    <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
                      {summary.systemCategories.map((category) => (
                        <div key={`system-${category.id}`} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{category.name}</div>
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">
                              Default system category · Position {category.position ?? 0}
                              {category.description ? ` · ${category.description}` : ""}
                            </div>
                          </div>
                          <UsageBadge count={category.usageCount} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>

              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Business Custom Categories</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      Tenant-local category records created and managed by this business.
                    </div>
                  </div>
                  {canCreateCategories ? (
                    <BtnSecondary onClick={openCreateCategory}>
                      <Plus className="h-4 w-4" /> Add Category
                    </BtnSecondary>
                  ) : null}
                </div>

                {!summary.customCategories.length ? (
                  <EmptyState
                    icon={FolderTree}
                    title="No custom categories yet"
                    description="Create a business-specific category when the default system categories are not enough for your merchandising structure."
                  />
                ) : (
                  summary.customCategories.map((category) => (
                    <div key={`custom-${category.id}`} className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950/60">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{category.name}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                            <span>Custom category</span>
                            <span>-</span>
                            <span>Position {category.position ?? 0}</span>
                          </div>
                          {category.description ? (
                            <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{category.description}</div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <UsageBadge count={category.usageCount} />
                          <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Editable
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {canEditCategories ? (
                          <button
                            type="button"
                            onClick={() => openEditCategory(category)}
                            className="rounded-xl border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
                          >
                            Edit
                          </button>
                        ) : null}
                        {canArchiveCategories ? (
                          <button
                            type="button"
                            onClick={() => archiveCategory(category)}
                            className="rounded-xl border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/20"
                          >
                            Archive
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </section>
            </div>
          )}
        </Panel>

        <Panel title="Product Tags" subtitle="Reusable product-level labels for campaigns, themes, seasons, and flexible catalog descriptors.">
          {!canReadTags ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
              You do not have permission to view product tags.
            </div>
          ) : loading ? (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading tags...</div>
          ) : !summary.tags.length ? (
            <EmptyState
              icon={Tags}
              title="No reusable tags yet"
              description="Create business-local tags so products can carry flexible labels beyond the required primary category."
              action={(
                canCreateTags ? (
                  <BtnPrimary onClick={openCreateTag}>
                    <Plus className="h-4 w-4" /> New Tag
                  </BtnPrimary>
                ) : null
              )}
            />
          ) : (
            <div className="space-y-3">
              {summary.tags.map((tag) => (
                <div key={tag.id} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900/40">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{tag.name}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        Business-local product tag
                      </div>
                      {tag.description ? (
                        <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{tag.description}</div>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <UsageBadge count={tag.usageCount} />
                      <span className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
                        Reusable
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {canEditTags ? (
                      <button
                        type="button"
                        onClick={() => openEditTag(tag)}
                        className="rounded-xl border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
                      >
                        Edit
                      </button>
                    ) : null}
                    {canArchiveTags ? (
                      <button
                        type="button"
                        onClick={() => archiveTag(tag)}
                        className="rounded-xl border border-accent bg-accent-subtle/60 px-3 py-1.5 text-xs font-medium text-accent transition-colors hover:bg-accent-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                      >
                        Archive
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Products Missing Category" subtitle="Products that still need a valid primary category before the merchandising catalog is fully structured.">
        {!canReadProducts ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
            Product-category assignment visibility still depends on product read access.
          </div>
        ) : loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading products...</div>
        ) : !summary.uncategorizedProducts.length ? (
          <EmptyState
            icon={Tag}
            title="All products categorized"
            description="No product records are currently missing primary category assignment."
          />
        ) : (
          <div className="space-y-3">
            {summary.uncategorizedProducts.map((product) => (
              <ProductLinkCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </Panel>

      <FormModal
        open={categoryModalOpen}
        onClose={closeCategoryModal}
        title={editingCategory?.id ? "Edit Category" : "New Category"}
        size="sm"
        footer={(
          <>
            <BtnSecondary onClick={closeCategoryModal}>Cancel</BtnSecondary>
            <BtnPrimary onClick={saveCategory} disabled={savingCategory}>
              {savingCategory ? "Saving..." : editingCategory?.id ? "Save Changes" : "Create Category"}
            </BtnPrimary>
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Category Name
            </label>
            <input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((current) => ({ ...current, name: e.target.value }))}
              className={inputCls}
              maxLength={100}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Display Position
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={categoryForm.position}
              onChange={(e) => setCategoryForm((current) => ({ ...current, position: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Description
            </label>
            <textarea
              rows={4}
              value={categoryForm.description}
              onChange={(e) => setCategoryForm((current) => ({ ...current, description: e.target.value }))}
              className={textareaCls}
              maxLength={500}
            />
          </div>
        </div>
      </FormModal>
        </>
      )}

      <FormModal
        open={tagModalOpen}
        onClose={closeTagModal}
        title={editingTag?.id ? "Edit Tag" : "New Tag"}
        size="sm"
        footer={(
          <>
            <BtnSecondary onClick={closeTagModal}>Cancel</BtnSecondary>
            <BtnPrimary onClick={saveTag} disabled={savingTag}>
              {savingTag ? "Saving..." : editingTag?.id ? "Save Changes" : "Create Tag"}
            </BtnPrimary>
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Tag Name
            </label>
            <input
              value={tagForm.name}
              onChange={(e) => setTagForm((current) => ({ ...current, name: e.target.value }))}
              className={inputCls}
              maxLength={80}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Description
            </label>
            <textarea
              rows={4}
              value={tagForm.description}
              onChange={(e) => setTagForm((current) => ({ ...current, description: e.target.value }))}
              className={textareaCls}
              maxLength={255}
            />
          </div>
        </div>
      </FormModal>
    </div>
  );
}


